import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {MediaItemSizeType, repoURL} from '@Libraries/s3-library';
import {getDirPathForUserImage, getUserImageScreenFilename} from '@Libraries/user-image-library';
import {UserImageSource} from '@Libraries/user-image-source.constants';
import {getDirPathForPMWStockMedia, getPMWStockImageFilename} from '@Libraries/pmw-stock-media-library';
import type {PMWStockImageSourceType} from '@Libraries/pmw-stock-media-library.types';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {getWriteBucket} from '@Utils/s3.util';
import {isUserPremium} from '@Libraries/user.library';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import {EdgeType} from '@PosterWhiteboard/classes/item-effects.types';

export const ImageItemSource = {
  ...UserImageSource,
  ...PMWStockImageSource,
};

export type ImageItemSourceType = (typeof ImageItemSource)[keyof typeof ImageItemSource];

const USER_UPLOADED_IMAGE_SOURCES: ReadonlySet<ImageItemSourceType> = new Set([
  ImageItemSource.UPLOAD,
  ImageItemSource.BACKGROUND_UPLOAD,
  ImageItemSource.EMAIL_UPLOAD,
  ImageItemSource.DRAWING,
]);

/** True for media the user put into My Uploads themselves — as opposed to stock (getty/pixabay/pmw-*) or AI-generated sources that also flow through the temp-data-URL upload pipeline. */
export const isUserUploadedImageSource = (source: ImageItemSourceType): boolean => {
  return USER_UPLOADED_IMAGE_SOURCES.has(source);
};

const STOCK_IMAGE_SOURCES: ReadonlySet<ImageItemSourceType> = new Set([
  ImageItemSource.GETTY,
  ImageItemSource.GETTY_ILLUSTRATIONS,
  ImageItemSource.PIXABAY,
  ImageItemSource.PIXABAY_ILLUSTRATIONS,
  ImageItemSource.STORYBLOCKS,
  ImageItemSource.STORYBLOCKS_ILLUSTRATIONS,
]);

/** External stock providers whose adds can land instantly with a temp data URL while the real import runs in the background. */
export const isStockImageSource = (source: ImageItemSourceType): boolean => {
  return STOCK_IMAGE_SOURCES.has(source);
};

const TIER_SCREEN_IMAGE_EXTENSION = 'webp';
export const REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION = '_removedbg';
export const REMOVED_BG_IMAGE_EXTENSION = 'webp';
export const UPSCALED_USER_IMAGE_EXTENSION = 'webp';
export const UPSCALED_USER_IMAGE_VARIANT_SUFFIX = '_upscaled';
const GETTY_WATERMARK_FREE_SUFFIX = '_wf';

export enum ScreenTier {
  SMALL = 'SMALL',
  BIG = 'BIG',
}

export interface ScreenTeirDetail {
  s3NameSuffix: string;
  minTargetDimension: number;
  maxDimension: number;
}

type ScreenTeirDetails = Record<ScreenTier, ScreenTeirDetail>;

export const SCREEN_TIER_DETAILS: ScreenTeirDetails = {
  [ScreenTier.SMALL]: {
    s3NameSuffix: '_small',
    minTargetDimension: 0,
    maxDimension: 800,
  },
  [ScreenTier.BIG]: {
    s3NameSuffix: '_big',
    minTargetDimension: 801,
    maxDimension: 1600,
  },
};

export const getImageScreenURL = (hashedFilename: string, extension: string, source: ImageItemSourceType): string => {
  // Extracted getty stickers exist only in the tiered screen layout the item loader reads; the
  // legacy screen filename this function otherwise builds (bare object name, no tier suffix or
  // extension) has no matching repo object for them.
  if (source === PMWStockImageSource.PMW_EXTRACTED_GETTY_STICKER) {
    return getUrlForImageItemBigTierScreenSize(hashedFilename, source);
  }

  let fileName;
  if (isSourcePMWStockImage(source)) {
    fileName = getPMWStockImageFilename(hashedFilename);
  } else if (isSourceGetty(source)) {
    fileName = getGettyImageScreenFilename(hashedFilename, extension);
  } else {
    fileName = getUserImageScreenFilename(hashedFilename, extension);
  }

  return repoURL(`${getDirPathForImageItem(source, MediaItemSizeType.SCREEN)}/${fileName}`, getWriteBucket());
};

export const getUrlForImageItemBigTierScreenSize = (hashedFilename: string, source: ImageItemSourceType): string => {
  return getUrlForImageItemScreenTier(hashedFilename, source, ScreenTier.BIG);
};

export const getDirPathForImageItem = (source: ImageItemSourceType, sizeType: MediaItemSizeType): string => {
  if (isSourcePMWStockImage(source)) {
    return getDirPathForPMWStockMedia(source, sizeType);
  }

  return getDirPathForUserImage(sizeType);
};

export const getUrlForImageItemScreenTier = (hashedFilename: string, source: ImageItemSourceType, tier: ScreenTier, isBackgroundRemoved = false, isUpscaled = false): string => {
  const objectName = getS3ObjectNameForImage(hashedFilename, source, tier, isBackgroundRemoved, isUpscaled);

  const fileName = `${objectName}.${getCompatibleImageFileExtension(TIER_SCREEN_IMAGE_EXTENSION)}`;
  return repoURL(`${getDirPathForImageItem(source, MediaItemSizeType.SCREEN)}/${fileName}`, getWriteBucket());
};

export const getHighResUrlForImage = (hashedFilename: string, ext: string, source: string, isUpscaled = false): string => {
  const variantBaseName = getUpscaledVariantBaseName(hashedFilename, isUpscaled, source);
  if (variantBaseName !== null) {
    return repoURL(
      `${getDirPathForImageItem(source, MediaItemSizeType.HIGH_RES)}/${variantBaseName}${UPSCALED_USER_IMAGE_VARIANT_SUFFIX}.${UPSCALED_USER_IMAGE_EXTENSION}`,
      getWriteBucket()
    );
  }
  return repoURL(`${getDirPathForImageItem(source, MediaItemSizeType.HIGH_RES)}/${hashedFilename}.${ext}`, getWriteBucket());
};

export const getImageItemBigScreenSizeRemovedBackgroundS3Key = (hashedFilename: string, source: ImageItemSourceType, isUpscaled = false): string => {
  const objectName = getS3ObjectNameForImage(hashedFilename, source, ScreenTier.BIG, true, isUpscaled);
  return `${getDirPathForImageItem(source, MediaItemSizeType.SCREEN)}/${objectName}.${REMOVED_BG_IMAGE_EXTENSION}`;
};

export const getRemoveBGPostFix = (): string => {
  return `${REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION}.${getCompatibleImageFileExtension(REMOVED_BG_IMAGE_EXTENSION)}`;
};

export const isSourcePMWStockImage = (source: ImageItemSourceType): source is PMWStockImageSourceType => {
  return Object.values(PMWStockImageSource).includes(source);
};

export const isSourceGetty = (source: ImageItemSourceType): boolean => {
  return source === UserImageSource.GETTY;
};

export const getGettyImageScreenObjectName = (hashedFilename: string): string => {
  if (isUserPremium()) {
    return `${hashedFilename}${GETTY_WATERMARK_FREE_SUFFIX}`;
  }

  return hashedFilename;
};

export const getAccessibleUpscaledVariantBaseName = (hashedFilename: string, source: ImageItemSourceType): string | null => {
  if (!isSourceGetty(source)) {
    return hashedFilename;
  }
  return isUserPremium() ? `${hashedFilename}${GETTY_WATERMARK_FREE_SUFFIX}` : null;
};

export const getUpscaledVariantBaseName = (hashedFilename: string, isUpscaled: boolean, source: ImageItemSourceType): string | null => {
  if (!isUpscaled) {
    return null;
  }
  return getAccessibleUpscaledVariantBaseName(hashedFilename, source);
};

export const getGettyImageScreenFilename = (hashedFilename: string, extension: string): string => {
  return `${getGettyImageScreenObjectName(hashedFilename)}.${extension}`;
};

export const getS3ObjectNameForImage = (hashedFilename: string, source: ImageItemSourceType, tier: ScreenTier, isBackgroundRemoved = false, isUpscaled = false): string => {
  const upscaledVariantBaseName = isUpscaled ? getAccessibleUpscaledVariantBaseName(hashedFilename, source) : null;

  let objectName = hashedFilename;
  if (upscaledVariantBaseName !== null) {
    objectName = upscaledVariantBaseName;
  } else if ((isSourceGetty(source) && !isBackgroundRemoved) || source === PMWStockImageSource.PMW_EXTRACTED_GETTY_STICKER) {
    objectName = getGettyImageScreenObjectName(hashedFilename);
  }

  const screenSizeSuffix = SCREEN_TIER_DETAILS[tier].s3NameSuffix;
  const variantSuffix = upscaledVariantBaseName !== null ? UPSCALED_USER_IMAGE_VARIANT_SUFFIX : '';
  const removeBgSuffix = isBackgroundRemoved && !isSourcePMWStockImage(source) ? REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION : '';

  return `${objectName}${variantSuffix}${removeBgSuffix}${screenSizeSuffix}`;
};

// Still stickers land on the canvas as ImageItems (only animated stickers are StickerItems), so sticker-ness
// of an image item is derived from its source.
export const isImageItemSourceSticker = (source: ImageItemSourceType): boolean => {
  return (
    source === ImageItemSource.PMW_STILL_STICKER ||
    source === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER ||
    source === ImageItemSource.PMW_AI_STICKER ||
    source === ImageItemSource.PMW_EMOJI
  );
};

export const isItemStickerImage = (item: Item): boolean => {
  return item.isImage() && isImageItemSourceSticker(item.imageSource);
};

/** Icons — stock or AI generated — land on the canvas as ImageItems, so icon-ness is derived from the source. */
export const isIconImageItem = (item: Item): boolean => {
  return item.isImage() && (item.isIcon() || item.isAiIcon());
};

/**
 * A crop, mask, edge, background removal or Eraser strokes: the image as shown differs in shape from the
 * file it was uploaded as, so a copy or an AI edit must start from the render rather than the file.
 */
export const hasImageShapeModifications = (item: ImageItemObject): boolean => {
  return item.cropData.cropped || item.masking.maskingItem !== null || item.effects.edgeType !== EdgeType.NONE || item.removeBackground.isBackgroundRemoved || item.refinement !== null;
};

export const doesSupportRemoveBackground = (source: ImageItemSourceType): boolean => {
  return !(
    source === ImageItemSource.PMW_STOCK_IMAGE ||
    source === ImageItemSource.PMW_ICON ||
    source === ImageItemSource.PMW_STILL_STICKER ||
    source === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER ||
    source === ImageItemSource.PMW_AI_STICKER ||
    source === ImageItemSource.PMW_AI_ICON
  );
};

export const doesSupportAiUpscale = (source: ImageItemSourceType): boolean => {
  return !isSourcePMWStockImage(source);
};
